/**
 * @file SectionActions.tsx
 * @description Действия с блоком в его панели (phase19.16 Р2;
 * uiux-design-r2 §6): [Дублировать] [Скрыть/Показать] и «⋯» — Выше · Ниже
 * · Удалить. `compact` (телефон, uiux §2 «шапка одной строкой») — только
 * «⋯» со всеми действиями. Каждое действие записывается в «Отменить».
 * «Сохранить как заготовку» не показываем до Р3 (SB28, SB103).
 * Р2а (шаблон страницы товара, uiux-design-r2a §6): блок с обязательными
 * элементами скрыть нельзя (кнопка неактивна с причиной), «Удалить» —
 * окно «Перенести и удалить блок» (`useTemplateGuards`), «Дублировать» —
 * копия без элементов товара.
 */

import { ArrowDown, ArrowUp, Copy, Eye, EyeOff, Trash2 } from "lucide-react";

import { Button } from "../../ui/button";
import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import { useBuilder } from "../builder-context";
import { blockTitle, isSectionBlock } from "../../site-pages/block-meta";
import type { SitePageBlock } from "../../../lib/api-site-pages";
import { blocksFullText } from "../../../lib/site-builder/block-limits";
import { REQUIRED_BLOCK_HIDE_TEXT, TEMPLATE_DUPLICATE_HINT } from "../../../lib/site-builder/product-element-meta";
import { blockHasRequired } from "../../../lib/site-builder/template-rules";

export function SectionActions({ block, compact = false }: { block: SitePageBlock; compact?: boolean }) {
  const b = useBuilder();
  const blocks = b.pages.page?.blocks ?? [];
  const index = blocks.findIndex((x) => x.id === block.id);
  const template = b.pages.page?.kind === "TEMPLATE";
  const fullText = blocksFullText(b.pages.page);
  const full = fullText !== null;
  // Скрыть блок с обязательными элементами шаблона нельзя (показать — можно).
  const hideLocked = template && block.isEnabled && blockHasRequired(block);
  const dupHint = fullText ?? (template ? TEMPLATE_DUPLICATE_HINT : undefined);
  async function duplicate() {
    const id = await b.blocks.duplicate(block);
    if (id) await b.select({ sel: { kind: "block", id }, panel: null });
  }
  const toggle = () => void b.blocks.toggle(block, !block.isEnabled);
  const move: RowAction[] = [
    { key: "up", label: "Выше", icon: ArrowUp, disabled: index <= 0, run: () => void b.blocks.move(block, -1) },
    { key: "down", label: "Ниже", icon: ArrowDown, disabled: index < 0 || index >= blocks.length - 1, run: () => void b.blocks.move(block, 1) },
    { key: "del", label: "Удалить", icon: Trash2, danger: true, run: () => void b.blocks.remove(block) },
  ];
  const title = `Блок «${blockTitle(block)}»`;

  if (compact) {
    return (
      <RowActionsMenu
        title={title}
        actions={[
          ...(!isSectionBlock(block) ? [] : [{ key: "dup", label: "Дублировать", icon: Copy, disabled: full, hint: dupHint, run: () => void duplicate() }]),
          { key: "vis", label: block.isEnabled ? "Скрыть" : "Показать", icon: block.isEnabled ? EyeOff : Eye, disabled: hideLocked, hint: hideLocked ? REQUIRED_BLOCK_HIDE_TEXT : undefined, run: toggle },
          ...move,
        ]}
      />
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      {!isSectionBlock(block) ? null : (
        <Button variant="outline" disabled={full} title={dupHint} onClick={() => void duplicate()}>
          <Copy aria-hidden />
          Дублировать
        </Button>
      )}
      <Button variant="outline" disabled={hideLocked} title={hideLocked ? REQUIRED_BLOCK_HIDE_TEXT : undefined} onClick={toggle}>
        {block.isEnabled ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
        {block.isEnabled ? "Скрыть" : "Показать"}
      </Button>
      <RowActionsMenu title={title} actions={move} />
      {template ? (
        <p className="type-caption basis-full text-on-surface-variant">
          {hideLocked ? `${REQUIRED_BLOCK_HIDE_TEXT} ` : ""}
          {TEMPLATE_DUPLICATE_HINT}
        </p>
      ) : null}
    </div>
  );
}
